// src/components/commercial/AmbassadorTerritories.jsx
//
// Admin page: draw and manage the ambassador territories (France > Paris >
// Paris 11 > Oberkampf...) and who prospects where. Backed by
// /admin/commercial/ambassador/territories (backend/routes/commercial/
// ambassadorAdmin.ts); saving a shape re-files the prospects it covers,
// the most specific territory winning.
//
// Drawing uses Leaflet-Geoman on the react-leaflet map: draw a polygon or
// a rectangle for a new territory, or "Modifier la forme" to move the
// corners of the selected one. Territories are indicative - an ambassador
// can still reserve outside theirs.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Alert, TextField, MenuItem, Switch,
  FormControlLabel, Checkbox, Divider, CircularProgress, Link, Chip,
} from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import useCommercialAdmin from '../../utils/useCommercialAdmin';
import { commercialGet, commercialPost, commercialPut } from '../../utils/commercialApi';

const PARIS = [48.8566, 2.3522];
const BLUE = '#0b4dff';
const GREY = '#6b7280';
const EMPTY_FORM = { name: '', parentId: '', city: '', priority: 0, active: true, agentIds: [] };

/** Geoman's draw toolbar: polygon and rectangle only. A finished shape goes to onDrawn and leaves the map (the page draws it). */
function DrawControl({ enabled, onDrawn }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled) return undefined;
    map.pm.addControls({
      position: 'topleft', drawPolygon: true, drawRectangle: true, drawMarker: false, drawCircleMarker: false,
      drawPolyline: false, drawCircle: false, drawText: false, editMode: false, dragMode: false,
      cutPolygon: false, removalMode: false, rotateMode: false,
    });
    map.pm.setGlobalOptions({ allowSelfIntersection: false });
    const created = (e) => {
      onDrawn(e.layer.toGeoJSON().geometry);
      map.removeLayer(e.layer);
    };
    map.on('pm:create', created);
    return () => { map.off('pm:create', created); map.pm.removeControls(); };
  }, [map, enabled, onDrawn]);
  return null;
}

/** The selected Polygon with draggable corners; every edit is reported as GeoJSON. */
function EditableShape({ geometry, onChange }) {
  const map = useMap();
  useEffect(() => {
    const group = L.geoJSON(geometry, { style: { color: BLUE, weight: 3, fillOpacity: 0.15 } }).addTo(map);
    const layer = group.getLayers()[0];
    layer.pm.enable({ allowSelfIntersection: false });
    const changed = () => onChange(layer.toGeoJSON().geometry);
    layer.on('pm:edit', changed);
    return () => { layer.off('pm:edit', changed); layer.pm.disable(); map.removeLayer(group); };
    // Re-create only when another territory is being edited, not on each reported edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}

/** Fits the map to a geometry when the selection changes. */
function FitTo({ geometry, token }) {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    const b = L.geoJSON(geometry).getBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [30, 30], maxZoom: 16 });
  }, [map, token]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function AmbassadorTerritories() {
  const { ready, forbidden, loading: authLoading, getToken } = useCommercialAdmin();
  const [territories, setTerritories] = useState(null);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [selectedId, setSelectedId] = useState(null);   // null + creating = new territory
  const [creating, setCreating] = useState(false);
  const [editingShape, setEditingShape] = useState(false);
  const [draft, setDraft] = useState(null);             // geometry drawn or edited, not saved yet
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!ready) return;
    commercialGet('/ambassador/territories', getToken())
      .then((d) => setTerritories(d.territories))
      .catch((e) => setError(e.message));
    commercialGet('/agents', getToken())
      .then((d) => setAgents((d.agents || []).filter((a) => Number(a.active) === 1)))
      .catch(() => {});
  }, [ready, getToken]);
  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => new Map((territories || []).map((t) => [t.id, t])), [territories]);
  const selected = selectedId != null ? byId.get(selectedId) : null;

  // Tree order: each territory followed by its children, indented.
  const tree = useMemo(() => {
    const out = [];
    const walk = (parentId, depth) => (territories || [])
      .filter((t) => t.parentId === parentId)
      .forEach((t) => { out.push({ ...t, depth }); walk(t.id, depth + 1); });
    walk(null, 0);
    return out;
  }, [territories]);

  const select = (t) => {
    setCreating(false); setEditingShape(false); setDraft(null); setNotice(''); setError('');
    setSelectedId(t.id);
    setForm({
      name: t.name, parentId: t.parentId ?? '', city: t.city ?? '', priority: t.priority, active: t.active,
      agentIds: t.agents.map((a) => a.id),
    });
  };
  const startCreate = () => {
    setSelectedId(null); setEditingShape(false); setDraft(null); setNotice(''); setError('');
    setCreating(true);
    setForm(EMPTY_FORM);
  };
  const onDrawn = useCallback((geometry) => setDraft(geometry), []);

  const save = async () => {
    setSaving(true); setError(''); setNotice('');
    try {
      const body = {
        name: form.name, city: form.city || null, priority: Number(form.priority) || 0, active: form.active,
        parentId: form.parentId === '' ? null : Number(form.parentId),
        ...(draft ? { polygon: draft } : {}),
      };
      let id = selectedId;
      let reassigned;
      if (creating) {
        if (!draft) throw new Error('Dessinez d’abord le territoire sur la carte.');
        const r = await commercialPost('/ambassador/territories', getToken(), body);
        id = r.id; reassigned = r.reassigned;
      } else {
        reassigned = (await commercialPut(`/ambassador/territories/${id}`, getToken(), body)).reassigned;
      }
      await commercialPut(`/ambassador/territories/${id}/agents`, getToken(), { agentIds: form.agentIds });
      setNotice(`Territoire enregistré. ${reassigned} prospect(s) rattaché(s) à un nouveau territoire.`);
      setCreating(false); setEditingShape(false); setDraft(null); setSelectedId(id);
      load();
    } catch (e) {
      setError(e.message === 'INVALID_POLYGON' ? 'Forme invalide (les côtés ne doivent pas se croiser).'
        : e.message === 'PARENT_LOOP' ? 'Un territoire ne peut pas être son propre parent (même indirectement).'
          : e.message === 'NAME_REQUIRED' ? 'Le nom est obligatoire.' : e.message);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Accès réservé aux administrateurs.</Alert></Container>;

  const shownGeometry = draft ?? selected?.polygon ?? null;
  const canEditShape = !creating && selected?.polygon?.type === 'Polygon';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Territoires des ambassadeurs</Typography>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/admin/commercial/ambassador/prospects">Prospects</Button>
          <Button component={RouterLink} to="/admin/commercial">← Dashboard</Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Un territoire indique où un ambassadeur prospecte en priorité ; il peut quand même réserver ailleurs.
        Un établissement appartient au territoire le plus précis qui le contient.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr 320px' }, gap: 2 }}>
        {/* Tree */}
        <Paper variant="outlined" sx={{ p: 1, maxHeight: 620, overflow: 'auto' }}>
          <Button fullWidth variant="contained" sx={{ mb: 1 }} onClick={startCreate}>+ Nouveau territoire</Button>
          {territories == null && <CircularProgress size={20} sx={{ m: 2 }} />}
          {territories?.length === 0 && <Typography variant="body2" sx={{ p: 1 }}>Aucun territoire pour l’instant.</Typography>}
          {tree.map((t) => (
            <Box
              key={t.id}
              onClick={() => select(t)}
              sx={{
                pl: 1 + t.depth * 2, py: 0.75, pr: 1, borderRadius: 1, cursor: 'pointer',
                bgcolor: t.id === selectedId ? 'action.selected' : 'transparent',
                opacity: t.active ? 1 : 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: t.id === selectedId ? 700 : 400 }}>
                {t.name}{!t.active && ' (inactif)'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t.prospects} prospect(s){t.agents.length ? ` · ${t.agents.map((a) => a.name).join(', ')}` : ''}
              </Typography>
            </Box>
          ))}
        </Paper>

        {/* Map */}
        <Paper variant="outlined" sx={{ height: 620, overflow: 'hidden' }}>
          <MapContainer center={PARIS} zoom={12} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            {(territories || []).filter((t) => t.polygon && t.active && t.id !== selectedId).map((t) => (
              <GeoJSON
                key={`${t.id}-${JSON.stringify(t.polygon).length}`}
                data={t.polygon}
                style={{ color: GREY, weight: 1, dashArray: t.parentId ? '4 4' : undefined, fillOpacity: 0.05 }}
                eventHandlers={{ click: () => select(t) }}
              />
            ))}
            {shownGeometry && !editingShape && (
              <GeoJSON key={`sel-${selectedId}-${JSON.stringify(shownGeometry)}`} data={shownGeometry}
                style={{ color: BLUE, weight: 3, fillOpacity: 0.15 }} />
            )}
            {editingShape && canEditShape && (
              <EditableShape key={`edit-${selectedId}`} geometry={selected.polygon} onChange={onDrawn} />
            )}
            <DrawControl enabled={creating} onDrawn={onDrawn} />
            <FitTo geometry={selected?.polygon} token={selectedId} />
          </MapContainer>
        </Paper>

        {/* Form */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {!creating && !selected && (
            <Typography variant="body2" color="text.secondary">
              Choisissez un territoire dans la liste ou sur la carte, ou créez-en un nouveau.
            </Typography>
          )}
          {(creating || selected) && (
            <Stack spacing={2}>
              <Typography variant="h6">{creating ? 'Nouveau territoire' : selected.name}</Typography>
              {creating && (
                <Alert severity={draft ? 'success' : 'info'}>
                  {draft ? 'Forme dessinée. Vous pouvez la redessiner avant d’enregistrer.'
                    : 'Dessinez le territoire avec l’outil polygone ou rectangle, en haut à gauche de la carte.'}
                </Alert>
              )}
              <TextField label="Nom" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField select label="Fait partie de" size="small" value={form.parentId}
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
                <MenuItem value="">— aucun (niveau le plus haut) —</MenuItem>
                {tree.filter((t) => t.id !== selectedId).map((t) => (
                  <MenuItem key={t.id} value={t.id}>{'  '.repeat(t.depth)}{t.name}</MenuItem>
                ))}
              </TextField>
              <TextField label="Ville" size="small" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <TextField label="Priorité (départage deux territoires de même niveau)" size="small" type="number"
                value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
              <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label={form.active ? 'Actif' : 'Inactif (ses prospects passent au territoire parent)'} />
              {!creating && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {canEditShape ? (
                    <Button size="small" variant="outlined" onClick={() => { setEditingShape(!editingShape); if (editingShape) setDraft(null); }}>
                      {editingShape ? 'Annuler la modification' : 'Modifier la forme'}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Forme en plusieurs parties : non modifiable ici.</Typography>
                  )}
                  {draft && <Chip size="small" color="warning" label="forme modifiée" />}
                </Stack>
              )}
              <Divider />
              <Typography variant="subtitle2">Ambassadeurs</Typography>
              {agents.length === 0 && <Typography variant="body2" color="text.secondary">Aucun ambassadeur actif.</Typography>}
              <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                {agents.map((a) => (
                  <FormControlLabel
                    key={a.id}
                    sx={{ display: 'flex' }}
                    control={(
                      <Checkbox size="small" checked={form.agentIds.includes(a.id)}
                        onChange={(e) => setForm({
                          ...form,
                          agentIds: e.target.checked ? [...form.agentIds, a.id] : form.agentIds.filter((x) => x !== a.id),
                        })} />
                    )}
                    label={`${a.first_name} ${a.last_name}`}
                  />
                ))}
              </Box>
              {selected && (
                <Typography variant="body2" color="text.secondary">
                  {selected.prospects} prospect(s), dont {selected.customers} déjà client(s).{' '}
                  <Link component={RouterLink} to={`/admin/commercial/ambassador/prospects?territoryId=${selected.id}`}>Voir</Link>
                </Typography>
              )}
              <Button variant="contained" disabled={saving || !form.name.trim() || (creating && !draft)} onClick={save}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Stack>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
